import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useParams } from 'react-router-dom'

// material-ui
import { styled, useTheme } from '@mui/material/styles'
import { AppBar, Box, CssBaseline, Toolbar } from '@mui/material'

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs'
import Header from './Header'
import Sidebar from './Sidebar'
import navigation from 'menu-items'
import { drawerWidth, smallDrawerWidth } from 'store/constant'
import { SET_MENU, SET_OFFICE } from 'store/actions'

// assets
import { IconChevronRight } from '@tabler/icons'

import useReduxAction from 'hooks/useReduxAction'
import { useSetState } from '@contenthouse-io/hooks'
import useUser from 'hooks/useUser'

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  ...(!open && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up('md')]: {
      marginLeft: 0,
      width: `calc(100% - ${smallDrawerWidth}px)`,
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px',
      width: `calc(100% - ${smallDrawerWidth}px)`,
      padding: '16px',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px',
      width: `calc(100% - ${smallDrawerWidth}px)`,
      padding: '16px',
      marginRight: '10px',
    },
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px',
    },
  }),
}))

// ==============================|| MAIN LAYOUT ||============================== //
const defaultState = {
  officeToLoad: null,
  officesToLoad: [],
  refreshOfficeUsers: 0,
}

const MainLayout = () => {
  const [state, setState] = useSetState(defaultState)
  const { officeToLoad, officesToLoad, refreshOfficeUsers } = state

  const theme = useTheme()

  const [project, setProject] = useState(null)

  const { currentUser, opened, office } = useSelector((state) => state.customization)
  const { id: projectId } = useParams()

  const { users, projects } = useSelector((reduxState) => reduxState.entities)

  const dispatch = useDispatch()

  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !opened })
  }

  const handleOfficeToggle = (value) => {
    dispatch({ type: SET_OFFICE, office: value })
  }

  useReduxAction('enums', 'loadEnums', 'MerchandiseGroup', [])
  useReduxAction('enums', 'loadEnums', 'PartType', [])
  useReduxAction('enums', 'loadEnums', 'PartSubType', [])
  useReduxAction('enums', 'loadEnums', 'ServiceType', [])
  useReduxAction('enums', 'loadEnums', 'UserRole', [])
  // unique enums
  useReduxAction('enums', 'loadEnums', 'State', [])
  useReduxAction('enums', 'loadEnums', 'PlatformType', [])

  useReduxAction(
    'offices',
    'loadOffices',
    {
      s: -1,
      ...(currentUser?.client_admin?.offices?.length && {
        f: `id|in|${currentUser.client_admin.offices.toString()}`,
      }),
    },
    []
  )

  useReduxAction('projects', 'loadProject', {}, [projectId], {
    dispatchAction: (action, requestOptions) => action(projectId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { errors, loaded, loading } = entityReducer
      return !loaded && !loading && !errors.length && projectId && !currentUser?.roles?.includes('Agent')
    },
  })

  const { loading: userLoading } = useUser()

  useReduxAction('users', 'loadOfficeUsers', { s: -1 }, [refreshOfficeUsers], {
    dispatchAction: (action, requestOptions) => action(officeToLoad, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { errors, loading } = entityReducer
      const execute = !loading && !errors.length && officeToLoad && refreshOfficeUsers > 0
      if (execute) {
        const remainingOffices = officesToLoad?.filter((x) => x !== officeToLoad)
        setState({ officesToLoad: remainingOffices })
      }
      return execute
    },
  })

  useEffect(() => {
    // Triggers loadOfficeUsers first call
    if (!userLoading && officeToLoad === null) {
      if (currentUser?.roles?.includes('Client_Admin')) {
        setState({ officesToLoad: currentUser?.client_admin?.offices || [] })
      } else if (office) {
        setState({ officeToLoad: office, refreshOfficeUsers: refreshOfficeUsers + 1 })
      }
    }
  }, [office, currentUser, userLoading])

  useEffect(() => {
    // For Client_Admin users, we need to load the remaining offices
    if (officesToLoad?.length > 0 && !userLoading) {
      const toLoad = officesToLoad[0]
      setState({ officeToLoad: toLoad, refreshOfficeUsers: refreshOfficeUsers + 1 })
    }
  }, [officesToLoad, userLoading])

  useEffect(() => {
    // Load office users whenever the office changes
    if (office !== officeToLoad && !userLoading && officeToLoad !== null && !currentUser?.roles?.includes('Client_Admin')) {
      setState({ officeToLoad: office, refreshOfficeUsers: refreshOfficeUsers + 1 })
    }
  }, [office, userLoading])

  useReduxAction('zones', 'loadZones', { s: -1 }, [])

  const prevOffice = useRef(office)

  const setPrevOffice = (newValue) => {
    prevOffice.current = newValue
  }

  useEffect(() => {
    if (!currentUser?.roles?.includes('Agent') && projectId && Object.values(projects).find((project) => project?.id === projectId)) {
      setProject(Object.values(projects).find((project) => project.id === projectId))
    }
  }, [projects])

  useEffect(() => {
    const projectOffice = project?.office || users[project?.owner]?.agent?.office
    const isAgent = currentUser?.roles?.includes('Agent')
    const isAssistant = currentUser?.roles?.includes('Assistant')

    let targetOffice = projectOffice
    if (isAgent) targetOffice = currentUser?.agent?.office
    if (isAssistant) targetOffice = currentUser?.assistant?.office

    if (!office && office !== targetOffice) handleOfficeToggle(targetOffice)
  }, [users])

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: opened ? theme.transitions.create('width') : 'none',
        }}
      >
        <Toolbar>
          <Header
            office={office}
            handleLeftDrawerToggle={handleLeftDrawerToggle}
            handleOfficeToggle={(value) => handleOfficeToggle(value)}
          />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={opened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main theme={theme} open={opened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet context={{ office, prevOffice, setPrevOffice }} />
      </Main>
    </Box>
  )
}

export default MainLayout
